import { Header } from '@everlywell/leaves';
import { SkeletonUnit, SkeletonLoader, colors } from '@everlywell/leaves';
import loadable from '@loadable/component';
import { Theme, ThemeContext } from 'components/Themable';
import Cookies from 'js-cookie';
import React, {
  Context,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { ariesTheme } from 'styles/ariesTheme';
import GlobalStyle from 'styles/global';
import { Spinner } from 'styles/utils';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import ewAnalytics from 'utils/analytics';
import { resetSession } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { CookieNames } from 'utils/constants/cookies';
import { QUERY_PARAMS } from 'utils/constants/queryParams';
import { STORE_ROOT, RESULTS_ROOT } from 'utils/constants/urls';
import { updateCJEvent } from 'utils/helpers/marketing/cj';
import { initJustUno } from 'utils/helpers/thirdPartyScripts';
import { TestPlanProvider } from 'utils/hooks/useTestPlan/context';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { ProductsBySlugType } from 'utils/types';

import { initialState, reducer } from '../../containers/CartContainer/store';
import { setShowTOS } from '../../containers/CartContainer/store/actions';
import { NotificationContextContainer } from '../../containers/Notification';
import image from '../../images/logo/everlywell-logo.svg';
import * as S from './styles';

declare global {
  interface Window {
    appRoot: string;
    asset_host: string;
  }
}

const mainElement = 'main';

const CartContainer = loadable(() => import('../../containers/CartContainer'), {
  fallback: (
    <S.CartSkeletonWrapper>
      <SkeletonLoader
        width={{
          value: 28,
          unit: SkeletonUnit.Pixel,
        }}
        height={{
          value: 28,
          unit: SkeletonUnit.Pixel,
        }}
      ></SkeletonLoader>
    </S.CartSkeletonWrapper>
  ),
});

const Footer = loadable(() => import('../Footer'), {
  fallback: <Spinner />,
});

type Props = {
  children: React.ReactNode;
  productsBySlug?: ProductsBySlugType;
  viewEventId?: string;
  productId?: number;
  footerRef?: React.Ref<HTMLDivElement>;
};
interface CartContextType {
  isOpen: boolean;
  setTOS: (isSet: boolean) => void;
  isCartLoaded: boolean;
}

export const CartContext: Context<CartContextType> =
  createContext<CartContextType>({
    setTOS: () => undefined,
    isOpen: false,
    isCartLoaded: false,
  });

export const ProductInfoContext = createContext<ProductsBySlugType>({});

const accountLinks = {
  label: 'Account',
  results: {
    label: 'Test Results',
    url: `${RESULTS_ROOT}/dashboard`,
  },
  settings: {
    label: 'Settings',
    url: `${RESULTS_ROOT}/account/settings`,
  },
  logout: {
    label: 'Log Out',
    url: `${STORE_ROOT}/logout`,
  },
  membership: {
    label: 'My Membership',
  },
};

// backend response still returns `sti` as the membershipType on the user
// we need to map that to its actual name for use with creating links to the page
const membershipNames: { [key: string]: 'current' | 'control' } = {
  sti: 'current',
  control: 'control',
};

const CustomGatsbyLink = ({
  href,
  children,
  ...rest
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <S.HeaderLinkWrapper
    {...rest}
    to={href}
    activeClassName="is-active"
    activeStyle={{
      color: `${colors.green3}`,
    }}
  >
    {children}
  </S.HeaderLinkWrapper>
);

const Layout = (props: Props): JSX.Element => {
  const [cartState, dispatch] = useReducer(reducer, initialState);
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  const { isOpen } = cartState;
  const cartProps = { cartState, dispatch, setIsCartLoaded };
  const headerRef = useRef<HTMLAnchorElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);
  const [cJEventParam] = useQueryParam('cjevent', StringParam);
  const [campaignId] = useQueryParam(
    QUERY_PARAMS.MARKETING.campaignId,
    NumberParam,
  );
  const {
    runEffect,
    userData: {
      fetched,
      loggedIn,
      membershipType,
      membershipState,
      activeCredits,
      userId,
    },
  } = useContext(UserContext);
  const { productsBySlug = {}, viewEventId, productId } = props;

  useEffect(() => {
    initJustUno();
    cJEventParam && updateCJEvent(cJEventParam);

    if (campaignId) {
      Cookies.set(CookieNames.IterableEmailCampaignId, campaignId.toString());
    }

    runEffect(viewEventId, productId);
  }, []);

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const handleTrackableLinkClick = (e: Event) => {
    ewAnalytics.eventHandler(e);
  };

  const handleLogout = (userId: number | string | null) => {
    if (userId) {
      ewAnalytics.setUserInfo({
        loggedIn: false,
        id: userId,
      });
    }
    resetSession();
  };

  const trackMixpanelEvents = (eventTitle: string) => {
    setSubMenuOpen(true);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: eventTitle,
      },
    });
  };

  const handleMenuOpen = () => {
    if (!menuOpen) {
      setMenuOpen(true);
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: `Site Nav (Desktop) - Shop`,
        },
      });
    }
  };

  // links defined in the Header have data-* attributes that get picked up in order to track via Mixpanel
  // the links aren't mounted when the header is closed, so we listen for the header drawer to open, and attach/remove listeners appropriately
  useEffect(() => {
    let trackableLinks: NodeListOf<HTMLAnchorElement> | undefined;

    if (menuOpen || subMenuOpen) {
      trackableLinks = headerRef?.current?.querySelectorAll(
        '[data-analytics-event]',
      );
      if (trackableLinks) {
        trackableLinks.forEach((link) =>
          link.addEventListener('click', handleTrackableLinkClick),
        );
      }
    }
    return () => {
      if (trackableLinks) {
        trackableLinks.forEach((link) =>
          link.removeEventListener('click', handleTrackableLinkClick),
        );
      }
    };
  }, [menuOpen, subMenuOpen]);

  return (
    <div id="app" className="siteRoot">
      <GlobalStyle />
      <ThemeProvider theme={ariesTheme}>
        <ThemeContext.Provider value={Theme.Default}>
          <ProductInfoContext.Provider value={productsBySlug}>
            <S.SkipLink href={`#${mainElement}`}>Skip to content</S.SkipLink>
            <Header
              loggedIn={loggedIn}
              isLoggingIn={!fetched}
              logoSrc={image}
              logoAlt="Everlywell"
              id="everlywellHeader"
              ref={headerRef}
              onMenuItemClick={trackMixpanelEvents}
              cartComponent={<CartContainer {...cartProps} />}
              accountLinks={accountLinks}
              onMenuOpen={handleMenuOpen}
              onMenuClose={() => setMenuOpen(false)}
              onSubMenuOpen={() => setSubMenuOpen(true)}
              onSubMenuClose={() => setSubMenuOpen(false)}
              onLogout={() => handleLogout(userId)}
              activeMembership={
                membershipType ? membershipNames[membershipType] : undefined
              }
              numOfMemberCredits={activeCredits}
              membershipState={membershipState}
              renderLinkAs={CustomGatsbyLink}
            />
            <S.Layout role="main" id={mainElement}>
              <NotificationContextContainer>
                <TestPlanProvider>
                  <CartContext.Provider
                    value={{ setTOS, isOpen, isCartLoaded }}
                  >
                    {props.children}
                  </CartContext.Provider>
                  {isOpen ? (
                    <S.SolvvyHiddenStyles />
                  ) : (
                    <S.SolvvyOverrideStyles />
                  )}
                </TestPlanProvider>
              </NotificationContextContainer>
            </S.Layout>
            <Footer ref={props.footerRef} />
          </ProductInfoContext.Provider>
        </ThemeContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export default Layout;
